import React, { useMemo, useState } from 'react';
import { Building2, Plus, Power, ShieldAlert } from 'lucide-react';
import { useBusinessContext } from '../../context/BusinessContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { Input } from '../../components/ui/Input';
import {
  useMerchantConfigs,
  useUpsertMerchantConfig,
  useDeactivateMerchantConfig,
  type MerchantConfig as MerchantConfigRow,
} from '../../hooks/useMerchantConfig';

/**
 * SAM-535 — which CyberSource account each business settles into.
 *
 * Until now this was an INSERT someone ran by hand in Postgres.
 *
 * What this screen edits is a POINTER: a row says "this business, in this environment, uses the
 * credentials in that Secrets Manager secret". The credentials themselves never pass through the
 * browser or the API — whoever holds the CyberSource account creates the secret separately, and
 * this page only names it. That is the whole reason merchant_config exists, so there is
 * deliberately no field here for an API key or a shared secret.
 *
 * Visibility is gated on the `xhinola_admin` group, but the real check is the server's, which
 * re-reads that group from the token. Hiding the page is courtesy; the 403 is the boundary.
 */

const ENVIRONMENTS: Array<'dev' | 'prod'> = ['dev', 'prod'];

const TENANT_WIDE = '__tenant_wide__';

/**
 * Which CyberSource host a merchant reaches. NOT the same thing as the environment above: that says
 * which SAM environment the row is for, this says which gateway it talks to. They are separate so a
 * payment can be run from dev against the live gateway to verify a real merchant profile.
 */
const GATEWAY_MODES: Array<{ value: 'test' | 'live'; label: string; host: string }> = [
  { value: 'test', label: 'Test', host: 'apitest.cybersource.com' },
  { value: 'live', label: 'Production', host: 'api.cybersource.com' },
];

export const MerchantConfig: React.FC = () => {
  const permissions = useUserPermissions();
  // SAM-535: this screen must be able to point ANY business in the tenant at a
  // CyberSource account, not just the ones tied to the caller's own Cognito business_id
  // claim — so it uses the unfiltered `tenantBusinesses` list, not `availableBusinesses`.
  // The backend independently verifies tenant membership (merchant_admin._assert_business_in_tenant).
  const { tenantBusinesses } = useBusinessContext();
  const canManage = permissions.isXhinolaAdmin;

  const { data: configs = [], isLoading, isError, error } = useMerchantConfigs(canManage);
  const upsert = useUpsertMerchantConfig();
  const deactivate = useDeactivateMerchantConfig();

  const [businessId, setBusinessId] = useState<string>(TENANT_WIDE);
  const [environment, setEnvironment] = useState<'dev' | 'prod'>('dev');
  const [secretName, setSecretName] = useState('');
  const [gatewayMode, setGatewayMode] = useState<'test' | 'live'>('test');
  const [notes, setNotes] = useState('');
  const [businessFilter, setBusinessFilter] = useState('');

  const filteredBusinesses = useMemo(() => {
    const query = businessFilter.trim().toLowerCase();
    if (!query) return tenantBusinesses;
    // The chosen business always stays in the list, otherwise the select would silently
    // display a different option than the one the form is about to save.
    return tenantBusinesses.filter((b) => b.id === businessId || b.name.toLowerCase().includes(query));
  }, [tenantBusinesses, businessFilter, businessId]);

  if (permissions.isLoading) return null;

  if (!canManage) {
    return (
      <div className="p-6">
        <div
          className="rounded-lg p-4 flex items-start gap-3"
          style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}
        >
          <ShieldAlert size={18} style={{ color: 'var(--ink-400)' }} className="shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Merchant configuration is Xhinola-only</p>
            <p className="text-xs mt-1" style={{ color: 'var(--ink-500)' }}>
              Choosing which account a business settles into decides where its money goes, so it is
              restricted to Xhinola operators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const activeConfigs = configs.filter((c) => c.active);
  const historicConfigs = configs.filter((c) => !c.active);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!secretName.trim()) return;
    upsert.mutate(
      {
        business_id: businessId === TENANT_WIDE ? null : businessId,
        environment,
        secret_name: secretName.trim(),
        gateway_mode: gatewayMode,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          setSecretName('');
          setNotes('');
          // The gateway is left as chosen: configuring several businesses in a row almost always
          // means the same gateway each time, and silently resetting it to Test would be the kind
          // of surprise that ends with a real charge.
          setGatewayMode(gatewayMode);
        },
      },
    );
  };

  const nameFor = (row: MerchantConfigRow) =>
    row.business_name
    || tenantBusinesses.find((b) => b.id === row.business_id)?.name
    || (row.business_id ? row.business_id : 'Every business without one of its own');

  return (
    <div className="p-6 max-w-4xl">
      {/* SAM-1312 (U29): named like its menu item and the size of its sibling pages' titles */}
      <h1 className="m-0 mb-1 text-[32px] leading-[40px] font-medium">Merchant Configuration</h1>
      <p className="text-xs mb-5" style={{ color: 'var(--ink-500)' }}>
        Which CyberSource account each business settles into. A row names a secret; the credentials
        live in Secrets Manager and never pass through here.
      </p>

      <form
        onSubmit={handleSave}
        className="rounded-lg p-4 mb-6"
        style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}
      >
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>Business</span>
            <Input
              type="text"
              value={businessFilter}
              onChange={(e) => setBusinessFilter(e.target.value)}
              placeholder="Filter businesses…"
              className="text-xs"
            />
            <select
              value={businessId}
              onChange={(e) => setBusinessId(e.target.value)}
              className="rounded px-2 py-1.5 text-xs"
              style={{ border: '1px solid var(--line)' }}
            >
              {/* A business with no row of its own falls back to this one. */}
              <option value={TENANT_WIDE}>Tenant-wide default</option>
              {filteredBusinesses.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>Environment</span>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as 'dev' | 'prod')}
              className="rounded px-2 py-1.5 text-xs"
              style={{ border: '1px solid var(--line)' }}
            >
              {ENVIRONMENTS.map((env) => <option key={env} value={env}>{env}</option>)}
            </select>
            <span className="text-[11px]" style={{ color: 'var(--ink-400)' }}>
              Which SAM environment this row applies to.
            </span>
          </label>
        </div>

        <div className="mb-3">
          <span className="text-[11px] block mb-1" style={{ color: 'var(--ink-500)' }}>
            CyberSource gateway
          </span>
          <div className="flex gap-1.5">
            {GATEWAY_MODES.map((mode) => {
              const selected = gatewayMode === mode.value;
              const isLive = mode.value === 'live';
              return (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setGatewayMode(mode.value)}
                  aria-pressed={selected}
                  className="rounded px-2.5 py-1 text-xs"
                  style={{
                    border: selected
                      ? `1px solid ${isLive ? '#B91C1C' : 'var(--brand)'}`
                      : '1px solid var(--line)',
                    background: selected
                      ? (isLive ? '#FEF2F2' : 'var(--brand-weak, #EEF2FF)')
                      : 'transparent',
                    color: selected ? (isLive ? '#B91C1C' : 'var(--brand)') : 'var(--ink-500)',
                  }}
                >
                  {mode.label}
                </button>
              );
            })}
          </div>
          <span className="text-[11px] block mt-1" style={{ color: 'var(--ink-400)' }}>
            {GATEWAY_MODES.find((m) => m.value === gatewayMode)?.host}
            {gatewayMode === 'live'
              ? ' — real charges, on a real merchant account, whatever environment this row is for.'
              : ' — nothing here moves real money.'}
          </span>
        </div>

        <label className="flex flex-col gap-1 mb-3">
          <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>
            Secrets Manager secret name
          </span>
          <input
            type="text"
            value={secretName}
            onChange={(e) => setSecretName(e.target.value)}
            placeholder="sam/dev/cybersource/capcana"
            className="rounded px-2 py-1.5 text-xs font-mono"
            style={{ border: '1px solid var(--line)' }}
          />
          <span className="text-[11px]" style={{ color: 'var(--ink-400)' }}>
            The name of an existing secret — not its contents. Create the secret first.
          </span>
        </label>

        <label className="flex flex-col gap-1 mb-3">
          <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>Notes (optional)</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why this account, who asked for it"
            className="rounded px-2 py-1.5 text-xs"
            style={{ border: '1px solid var(--line)' }}
          />
        </label>

        <button
          type="submit"
          disabled={!secretName.trim() || upsert.isPending}
          className="btn"
          style={{ opacity: !secretName.trim() || upsert.isPending ? 0.5 : 1 }}
        >
          <Plus size={13} />
          {upsert.isPending ? 'Saving…' : 'Save configuration'}
        </button>
        <p className="text-[11px] mt-2" style={{ color: 'var(--ink-400)' }}>
          Saving over an existing pair replaces it: the previous row is kept, deactivated, so past
          payments can still be traced to the account they settled into.
        </p>
      </form>

      <h2 className="text-sm font-medium mb-2">Active</h2>
      {isLoading ? (
        <p className="text-xs" style={{ color: 'var(--ink-400)' }}>Loading…</p>
      ) : isError ? (
        <p className="text-xs" style={{ color: '#B91C1C' }}>{error?.message || 'Could not load'}</p>
      ) : activeConfigs.length === 0 ? (
        <p className="text-xs italic mb-4" style={{ color: 'var(--ink-400)' }}>
          None. Without an active configuration, payments are refused rather than sent to some
          default account.
        </p>
      ) : (
        <div className="space-y-2 mb-6">
          {activeConfigs.map((row) => (
            <div
              key={row.id}
              className="rounded-lg p-3 flex items-start justify-between gap-3"
              style={{ border: '1px solid var(--line)' }}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Building2 size={12} style={{ color: 'var(--ink-400)' }} />
                  <span className="text-xs font-medium truncate">{nameFor(row)}</span>
                  <span
                    className="text-[10px] rounded px-1.5 py-0.5"
                    style={{ background: 'var(--surface-2)', color: 'var(--ink-500)' }}
                  >
                    {row.environment}
                  </span>
                  {/* Only called out when it is the live gateway: that is the state worth
                      noticing, and a badge on every row would stop being read. */}
                  {row.gateway_mode === 'live' && (
                    <span
                      className="text-[10px] rounded px-1.5 py-0.5"
                      style={{ background: '#FEF2F2', color: '#B91C1C' }}
                      title="Charges go to the live CyberSource gateway"
                    >
                      live gateway
                    </span>
                  )}
                </div>
                <p className="text-[11px] font-mono mt-1 truncate" style={{ color: 'var(--ink-500)' }}>
                  {row.secret_name}
                </p>
                {row.notes && (
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--ink-400)' }}>{row.notes}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => deactivate.mutate(row.id)}
                disabled={deactivate.isPending}
                className="btn shrink-0"
                title="Stop using this account. The business falls back to the tenant-wide row, or stops taking payments if there isn't one."
              >
                <Power size={13} />
                Deactivate
              </button>
            </div>
          ))}
        </div>
      )}

      {historicConfigs.length > 0 && (
        <>
          <h2 className="text-sm font-medium mb-2">Previous</h2>
          <p className="text-[11px] mb-2" style={{ color: 'var(--ink-400)' }}>
            Kept because they explain where past payments settled.
          </p>
          <div className="space-y-1">
            {historicConfigs.map((row) => (
              <div
                key={row.id}
                className="rounded px-3 py-2 text-[11px] flex items-center gap-2"
                style={{ border: '1px solid var(--line)', color: 'var(--ink-400)' }}
              >
                <span className="truncate">{nameFor(row)}</span>
                <span>·</span>
                <span>{row.environment}</span>
                <span>·</span>
                <span className="font-mono truncate">{row.secret_name}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MerchantConfig;
